/**
 * Client for the Corpus Run dashboard API (task 4030.8): runs, metrics, files, jobs, and the
 * diagnostics bundle. Every call is a read; controls stay with the run API.
 */
import { apiFetch } from "../../app/api";

const BASE = "/api/v1/workflow/runs";

export type RunState =
  | "draft"
  | "running"
  | "pausing"
  | "paused"
  | "awaiting_operator"
  | "cancelling"
  | "cancelled"
  | "completed"
  | "failed";

export type ItemCategory =
  | "processed"
  | "duplicate"
  | "excluded"
  | "failed"
  | "held"
  | "unresolved"
  | "in_progress";

/** The populations the dashboard never folds into a success percentage (MER-D-021). */
export const FILE_POPULATIONS: ItemCategory[] = [
  "failed",
  "held",
  "duplicate",
  "excluded",
  "unresolved",
];
export const CATEGORIES: ItemCategory[] = ["processed", ...FILE_POPULATIONS, "in_progress"];
export const RUN_STATES: RunState[] = [
  "draft",
  "running",
  "pausing",
  "paused",
  "awaiting_operator",
  "cancelling",
  "cancelled",
  "completed",
  "failed",
];

export interface RunStageSummary {
  ordinal: number;
  stage: string;
  version: string;
  barrier: boolean;
  state: string;
}

export interface RunSummary {
  id: string;
  engagement_id: string;
  run_key: string;
  description: string | null;
  state: RunState;
  state_reason: string | null;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
  stages: RunStageSummary[];
}

export interface LevelCounts {
  level: "files" | "parsed_documents" | "segments" | "observations" | "claims" | "outputs";
  total: number;
  by_state: Record<string, number>;
}

export interface StageMetrics {
  ordinal: number;
  stage: string;
  version: string;
  barrier: boolean;
  state: string;
  counts: Record<string, number>;
  attempts: number;
  retried_jobs: number;
  extra_attempts: number;
  duration_p50_seconds: number | null;
  duration_p95_seconds: number | null;
  duration_max_seconds: number | null;
  first_enqueued_at: string | null;
  last_completed_at: string | null;
  error_classes: Record<string, number>;
  waiting_for_barrier: boolean;
  budget_request: string | null;
}

export interface DeadLetterRow {
  dead_letter_id: string;
  job_id: string;
  stage: string;
  subject_key: string;
  path: string | null;
  error_class: string;
  message: string | null;
  reason: string;
  attempts: number;
  state: string;
  created_at: string;
}

export interface WorkerHealth {
  worker_id: string;
  hostname: string | null;
  version: string | null;
  status: string;
  alive: boolean;
  last_seen_at: string | null;
  leases: number;
  expired_leases: number;
  attempts: number;
  succeeded: number;
  failed: number;
}

export interface Check {
  name: string;
  description: string;
  expected: number;
  actual: number;
  ok: boolean;
}

export interface Metrics {
  run_id: string;
  engagement_id: string;
  run_key: string;
  generated_at: string;
  run: {
    state: RunState;
    state_reason: string | null;
    terminal: boolean;
    stages: number;
    stages_finished: number;
    started_at: string | null;
    ended_at: string | null;
    elapsed_seconds: number | null;
  };
  files: Record<string, number>;
  total_files: number;
  awaiting_operator: number;
  inventory_entries: number;
  superseded_entries: number;
  orphan_jobs: number;
  levels: LevelCounts[];
  populations: { files: Record<string, number>; claims: Record<string, number> };
  stages: StageMetrics[];
  queue: {
    by_status: Record<string, number>;
    ready: number;
    delayed: number;
    oldest_ready_seconds: number | null;
    expired_leases: number;
  };
  retries: {
    retried_jobs: number;
    extra_attempts: number;
    retry_controls: number;
    requeued_dead_letters: number;
  };
  failures: {
    by_state: Record<string, number>;
    by_error_class: Record<string, number>;
    open: DeadLetterRow[];
  };
  budget: {
    hard_limit_usd: string | null;
    soft_limit_usd: string | null;
    provider_limits_usd: Record<string, string>;
    spent_usd: string;
    outstanding_usd: string;
    remaining_usd: string | null;
    calls: number;
    reservations: number;
    reservations_open: number;
    requests_open: number;
    open_requests: { request_id: string; kind: string; stage: string; detail: string }[];
  };
  workers: WorkerHealth[];
  dispositions: Record<string, number>;
  checks: Check[];
  reconciled: boolean;
}

export interface StageJob {
  job_id: string;
  stage_version: string;
  status: string;
  attempts: number;
  reused: boolean;
  error_class: string | null;
  dead_letter_id: string | null;
  dead_letter_state: string | null;
}

export interface FileRow {
  item_key: string;
  path: string;
  category: ItemCategory;
  reason_code: string;
  reason: string;
  entry_id: string | null;
  batch_id: string | null;
  stage: string | null;
  awaiting_operator: boolean;
  source_artifact_version_id: string | null;
  jobs: Record<string, StageJob>;
}

export interface Files {
  run_id: string;
  terminal: boolean;
  total: number;
  filtered: number;
  counts: Record<string, number>;
  stages: string[];
  items: FileRow[];
}

export interface FilesQuery {
  category?: ItemCategory;
  stage?: string;
  q?: string;
  awaiting_operator?: boolean;
  offset?: number;
  limit?: number;
}

export interface JobDetail {
  run_id: string;
  job_id: string;
  stage: string;
  stage_version: string;
  subject_key: string;
  path: string | null;
  status: string;
  attempts: number;
  failures: number;
  max_failures: number;
  priority: number;
  resource_class: string;
  provider: string | null;
  lease_owner: string | null;
  leased_at: string | null;
  heartbeat_at: string | null;
  lease_expires_at: string | null;
  available_at: string;
  created_at: string;
  completed_at: string | null;
  cache_key: string | null;
  reused_from_job_id: string | null;
  payload: Record<string, unknown>;
  result: Record<string, unknown> | null;
  last_error: Record<string, unknown> | null;
  attempt_history: {
    attempt_no: number;
    worker_id: string;
    stage_version: string;
    started_at: string;
    heartbeat_at: string;
    ended_at: string | null;
    outcome: string;
    error: Record<string, unknown> | null;
  }[];
  events: {
    seq: number;
    attempt_no: number | null;
    event: string;
    actor: string;
    details: Record<string, unknown>;
    at: string;
  }[];
  dead_letters: {
    dead_letter_id: string;
    error_class: string;
    error: Record<string, unknown>;
    reason: string;
    attempts: number;
    failures: number;
    state: string;
    created_at: string;
    resolved_by: string | null;
    resolution_reason: string | null;
    resolved_at: string | null;
  }[];
  file: FileRow | null;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export function listRuns(filter: { engagement_id?: string; state?: RunState } = {}) {
  return apiFetch<RunSummary[]>(`${BASE}${query(filter)}`);
}

export function getMetrics(runId: string) {
  return apiFetch<Metrics>(`${BASE}/${encodeURIComponent(runId)}/metrics`);
}

export function getFiles(runId: string, filter: FilesQuery = {}) {
  return apiFetch<Files>(`${BASE}/${encodeURIComponent(runId)}/files${query({ ...filter })}`);
}

export function getJob(runId: string, jobId: string) {
  return apiFetch<JobDetail>(
    `${BASE}/${encodeURIComponent(runId)}/jobs/${encodeURIComponent(jobId)}`,
  );
}

export function diagnosticsUrl(runId: string): string {
  return `${BASE}/${encodeURIComponent(runId)}/diagnostics`;
}

export function inventoryUrl(runId: string): string {
  return `/sources/inventory?run=${encodeURIComponent(runId)}`;
}

export function runPath(runId: string): string {
  return `/corpus-runs/${encodeURIComponent(runId)}`;
}

export function jobPath(runId: string, jobId: string): string {
  return `${runPath(runId)}/jobs/${encodeURIComponent(jobId)}`;
}

export function seconds(value: number | null): string {
  if (value === null) return "–";
  if (value < 60) return `${value.toFixed(value < 10 ? 2 : 1)} s`;
  const minutes = Math.floor(value / 60);
  if (minutes < 60) return `${minutes} min ${Math.round(value % 60)} s`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

export function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}
